'use client'
import { useEffect, useRef, type FormEvent, type RefObject } from 'react'

import { useRouter } from 'next/navigation'

import { useTranslation } from '@/hooks/client'
import SvgCircleNotch from '@/icons/regular/CircleNotch'
import SvgMagnifyingGlass02 from '@/icons/regular/MagnifyingGlass02'
import SvgXmark from '@/icons/regular/Xmark'
import { tw } from '@/lib/utils'
import { localizedHref } from '@/lib/utils/localized-href'

import { useNavContext } from '../nav-context-provider'

interface SheetSearchInputProps {
  term: string
  onChange: (term: string) => void
  onClear: () => void
  searching: boolean
  /** Focus the field on mount (the sheet's search screen); the launcher manages focus itself. */
  autoFocus?: boolean
  /** Lets the launcher's type-anywhere handler reach the field. */
  inputRef?: RefObject<HTMLInputElement | null>
  /** Replaces the form's padding. */
  className?: string
}

// The bordered 52px field. Enter goes to the full search page; the clear
// button and the spinner share the trailing slot.
export const SheetSearchInput = ({
  term,
  onChange,
  onClear,
  searching,
  autoFocus = true,
  inputRef,
  className = 'px-4 pt-1 pb-3',
}: SheetSearchInputProps) => {
  const { t, locale } = useTranslation()
  const router = useRouter()
  const { close } = useNavContext()
  const localRef = useRef<HTMLInputElement>(null)
  const ref = inputRef ?? localRef

  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus({ preventScroll: true })
    }
  }, [autoFocus, ref])

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = term.trim()
    if (!trimmed) {
      return
    }
    close()
    router.push(localizedHref(locale, `/search?q=${encodeURIComponent(trimmed)}`))
  }

  return (
    <form role="search" onSubmit={submit} className={tw('shrink-0', className)}>
      <div className="relative">
        <SvgMagnifyingGlass02
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-4 size-[20px] -translate-y-1/2 text-(--sh-link) [&_path]:stroke-current"
        />
        <input
          ref={ref}
          type="search"
          name="q"
          value={term}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t('nav.searchFullPlaceholder', 'Search hearing products, reviews, and more')}
          aria-label={t('nav.search', 'Search')}
          autoComplete="off"
          enterKeyHint="search"
          className="h-[52px] w-full appearance-none rounded-[12px] border border-(--sh-field-border) bg-(--sh-chip) pr-14 pl-12 text-[16px] text-(--sh-ink) transition-[border-color,box-shadow] placeholder:text-(--sh-ink-muted) focus:border-(--sh-link) focus:outline-none focus:[box-shadow:0_0_0_3px_var(--sh-focus-glow)] [&::-webkit-search-cancel-button]:hidden"
        />
        {searching ? (
          <SvgCircleNotch
            aria-hidden
            fill="currentColor"
            className="pointer-events-none absolute top-1/2 right-4 size-[20px] -translate-y-1/2 animate-spin text-(--sh-ink-soft)"
          />
        ) : term ? (
          <button
            type="button"
            onClick={onClear}
            aria-label={t('nav.sheet.clearSearch', 'Clear search')}
            data-tracking="ignore"
            className="absolute top-1/2 right-1 flex size-[44px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--sh-ink-soft)"
          >
            <SvgXmark aria-hidden fill="currentColor" className="size-[16px]" />
          </button>
        ) : null}
      </div>
      <p className="sr-only" aria-live="polite">
        {searching ? t('nav.sheet.searching', 'Searching…') : ''}
      </p>
    </form>
  )
}
